import React from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * PhoneFrame — กรอบมือถือสำหรับพรีวิวสิ่งที่จะขึ้นบนแอปลูกค้า
 *
 * ใช้ในหน้าที่คนหลังบ้านตั้งค่าเนื้อหาที่ลูกค้าจะเห็น (แบนเนอร์ · สิทธิประโยชน์ Tier · ของรางวัล)
 * เหตุผลที่ต้องมีพรีวิว: ข้อความสิทธิประโยชน์และภาพ 16:9 ถูกครอปและตัดบรรทัดบนจอ 390px
 * ถ้าไม่เห็นตอนตั้งค่า จะรู้ว่าข้อความยาวเกินก็หลังจากลูกค้าเห็นแล้ว
 *
 * ⚠ ข้อความ**ภายใน**กรอบนี้ต้องยึดกฎของแอปมือถือ ไม่ใช่กฎหลังบ้าน:
 *   ตัวอักษร ≥ 16px · line-height 1.6 · พื้นที่กด 44px
 *   (กฎ 13–14px ของหลังบ้านใช้กับ UI ของเจ้าหน้าที่เท่านั้น)
 */

export interface PhoneFrameProps {
  children: React.ReactNode;
  /** ความกว้างของหน้าจอในกรอบ — ค่าตั้งต้น 390px คือความกว้างที่แอปออกแบบไว้ */
  width?: number;
  /** ความสูงของพื้นที่เนื้อหา — ปล่อยว่างให้สูงตามเนื้อหา */
  height?: number;
  /** ชื่อหน้าที่พรีวิว เช่น 'หน้าแรก' */
  screenLabel?: string;
  /** คำกำกับใต้กรอบ */
  caption?: React.ReactNode;
  className?: string;
}

export const PhoneFrame: React.FC<PhoneFrameProps> = ({
  children, width = 390, height, screenLabel, caption, className,
}) => (
  <figure className={cn('m-0 flex flex-col items-center gap-[var(--space-2)]', className)}>
    <div
      aria-label={screenLabel ? `พรีวิวบนแอปมือถือ — ${screenLabel}` : 'พรีวิวบนแอปมือถือ'}
      role="img"
      className="overflow-hidden rounded-[28px] border-[8px] border-[var(--ink-900)] bg-[var(--surface-0)]"
      style={{ width: width + 16, boxShadow: 'var(--elevation-dropdown)' }}
    >
      {/* แถบสถานะจำลอง — ไม่ใช่ของจริง มีไว้ให้เห็นว่าเนื้อหาเริ่มที่ไหน */}
      <div
        className="flex items-center justify-between bg-[var(--ink-900)] px-[var(--space-4)] pb-[var(--space-1)] pt-[var(--space-05)]"
        style={{ width }}
      >
        <span className="bo-num text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-white">09:41</span>
        <span className="flex items-center gap-[3px] text-white">
          <Icon name="signal_cellular_alt" size={14} filled />
          <Icon name="wifi" size={14} filled />
          <Icon name="battery_full" size={14} filled />
        </span>
      </div>
      {screenLabel && (
        <div
          className="flex items-center gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-2)]"
          style={{ width }}
        >
          <Icon name="arrow_back" size={20} className="text-[var(--ink-900)]" />
          <span className="text-[length:var(--text-body)] font-[var(--weight-semibold)] leading-[1.6] text-[var(--ink-900)]">{screenLabel}</span>
        </div>
      )}
      <div
        className="overflow-y-auto bg-[var(--surface-0)]"
        style={{ width, height, maxHeight: height ?? 620 }}
      >
        {children}
      </div>
    </div>
    {caption && (
      <figcaption className="max-w-[420px] text-center text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
        {caption}
      </figcaption>
    )}
  </figure>
);
